import api from './api';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const batchScheduleService = {
  // Get all batches for dropdown
  getAllBatches: () => {
    return api.get('/batch-schedules/batches');
  },

  // Get schedule for a specific batch
  getBatchSchedule: (batchId) => {
    return api.get(`/batch-schedules/${batchId}`);
  },

  // Get monthly view for a batch
  getMonthlyView: (batchId, year, month) => {
    return api.get(`/batch-schedules/${batchId}/monthly/${year}/${month}`);
  },

  // Bulk upload schedule for a batch
  bulkUploadSchedule: (batchId, scheduleData) => {
    return api.post('/batch-schedules/bulk-upload', {
      batchId,
      scheduleData
    });
  },

  // Parse CSV data
  parseCsvSchedule: (csvData, batchId) => {
    return api.post('/batch-schedules/parse-csv', {
      csvData,
      batchId
    });
  },

  // Update individual schedule entry
  updateScheduleEntry: (entryId, updateData) => {
    return api.put(`/batch-schedules/entry/${entryId}`, updateData);
  },

  // Insert a session at a (week, day) slot, cascade-shifting later sessions.
  // Pass {dryRun: true} to preview the impact (affectedCount, overflow) without writing.
  // Pass confirmExtend:true in body to let the batch end_date grow if the shift
  // would push past the framework cap.
  insertSession: (batchId, body, { dryRun = false } = {}) => {
    const qs = dryRun ? '?dryRun=true' : '';
    return api.post(`/batch-schedules/${batchId}/insert${qs}`, body);
  },

  // Get batch progress statistics
  getBatchProgress: (batchId) => {
    return api.get(`/batch-schedules/${batchId}/progress`);
  },

  // Get trainer assignments for conflict detection
  getTrainerAssignments: (startDate, endDate) => {
    return api.get(`/batch-schedules/trainer-assignments?startDate=${startDate}&endDate=${endDate}`);
  },

  // Get schedules grouped by trainer (for trainer-wise view)
  getSchedulesByTrainer: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.batchId) queryParams.append('batchId', params.batchId);
    return api.get(`/batch-schedules/by-trainer?${queryParams.toString()}`);
  },

  // Get schedules grouped by center (for center-wise view)
  getSchedulesByCenter: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    return api.get(`/batch-schedules/by-center?${queryParams.toString()}`);
  },

  // Get schedules grouped by zone (for zone-wise view)
  getSchedulesByZone: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.weekStartDate) queryParams.append('weekStartDate', params.weekStartDate);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.includeCompleted) queryParams.append('includeCompleted', 'true');
    if (params.includeUpcoming) queryParams.append('includeUpcoming', 'true');
    return api.get(`/batch-schedules/by-zone?${queryParams.toString()}`);
  },

  // Recalculate session dates based on current holidays
  // Called after holidays are added/updated/deleted
  recalculateSessionDates: (batchId = null) => {
    const url = batchId
      ? `/batch-schedules/recalculate-dates?batchId=${batchId}`
      : '/batch-schedules/recalculate-dates';
    return api.post(url);
  },

  // Helper function to convert CSV text to array format using PapaParse
  parseCsvText: (csvText) => {
    console.log('=== PAPAPARSE INPUT ===');
    console.log('Raw CSV text:', JSON.stringify(csvText));

    const result = Papa.parse(csvText, {
      skipEmptyLines: true,
      trimHeaders: false, // Don't trim headers to preserve multi-line content
      transform: (value) => value // Don't trim values to preserve newlines
    });

    console.log('=== PAPAPARSE OUTPUT ===');
    console.log('Parsed result:', result);
    console.log('Parsed data:', result.data);

    if (result.errors.length > 0) {
      console.warn('CSV parsing errors:', result.errors);
    }

    return result.data;
  },

  // Get sheet names from an Excel file (for sheet selection UI)
  getExcelSheetNames: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          resolve(workbook.SheetNames);
        } catch (error) {
          console.error('Error reading Excel sheet names:', error);
          reject(error);
        }
      };

      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        reject(error);
      };

      reader.readAsArrayBuffer(file);
    });
  },

  // Parse Excel file and convert to schedule format
  // Excel format: Column headers = Week numbers, Rows = Day numbers (1-6)
  // Handles: title rows, continuation rows (null in first column), multiple sessions per cell
  // selectedSheet: optional sheet name to parse (defaults to first sheet)
  parseExcelFile: (file, selectedSheet = null) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          console.log('=== EXCEL PARSING START ===');
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          console.log('Workbook sheets:', workbook.SheetNames);

          // Use selected sheet or default to first sheet
          const sheetName = selectedSheet && workbook.SheetNames.includes(selectedSheet)
            ? selectedSheet
            : workbook.SheetNames[0];
          console.log('Using sheet:', sheetName);
          const worksheet = workbook.Sheets[sheetName];

          // Convert to JSON with header option
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          console.log('Raw Excel data (first 5 rows):', jsonData.slice(0, 5));

          if (jsonData.length < 2) {
            reject(new Error('Excel file must have headers and at least one row of data'));
            return;
          }

          // Find the header row (row containing "Week" in columns)
          let headerRowIndex = -1;
          for (let i = 0; i < Math.min(jsonData.length, 5); i++) {
            const row = jsonData[i];
            if (row && row.some(cell => cell && String(cell).toLowerCase().includes('week'))) {
              headerRowIndex = i;
              break;
            }
          }

          if (headerRowIndex === -1) {
            // Fallback: assume first row with multiple columns is header
            headerRowIndex = jsonData.findIndex(row => row && row.length > 2);
            if (headerRowIndex === -1) headerRowIndex = 0;
          }

          console.log('Header row index:', headerRowIndex);
          const headers = jsonData[headerRowIndex];
          console.log('Headers (weeks):', headers);

          // Parse week numbers from headers
          const weekColumns = [];
          headers.forEach((header, index) => {
            if (index === 0) return; // Skip first column (Day labels)

            // Extract week number from header (e.g., "Week 1", "1", "W1", etc.)
            const headerStr = String(header || '').trim();
            const weekMatch = headerStr.match(/week\s*(\d+)|^(\d+)$/i);
            if (weekMatch) {
              weekColumns.push({
                index,
                weekNumber: parseInt(weekMatch[1] || weekMatch[2])
              });
            }
          });

          console.log('Week columns:', weekColumns);

          if (weekColumns.length === 0) {
            reject(new Error('Could not find week columns in the Excel file. Make sure headers contain "Week 1", "Week 2", etc.'));
            return;
          }

          // Build schedule data structure
          const scheduleData = [];
          let currentDayNumber = 0;

          // Process each row after headers
          for (let rowIdx = headerRowIndex + 1; rowIdx < jsonData.length; rowIdx++) {
            const row = jsonData[rowIdx];
            if (!row || row.length === 0) continue;

            // Check if this row has a day number/label in first column
            const dayValue = row[0];
            let dayNumber;

            if (dayValue !== null && dayValue !== undefined && String(dayValue).trim() !== '') {
              // Extract day number from first column
              if (typeof dayValue === 'number') {
                dayNumber = dayValue;
              } else {
                const dayStr = String(dayValue).trim();
                const dayMatch = dayStr.match(/day\s*(\d+)|^(\d+)/i);
                if (dayMatch) {
                  dayNumber = parseInt(dayMatch[1] || dayMatch[2]);
                }
              }

              if (dayNumber && dayNumber >= 1 && dayNumber <= 6) {
                currentDayNumber = dayNumber;
              }
            }

            // Skip if we don't have a valid current day number
            if (currentDayNumber < 1 || currentDayNumber > 6) continue;

            // Process each week column for this day
            // Capture currentDayNumber in local scope to avoid loop-func warning
            const dayNum = currentDayNumber;
            weekColumns.forEach(({ index, weekNumber }) => {
              const cellValue = row[index];
              if (cellValue !== null && cellValue !== undefined && String(cellValue).trim()) {
                // Handle cells with multiple sessions (separated by newline)
                const content = String(cellValue).trim();
                const sessions = content.split(/\r?\n/).filter(s => s.trim());

                sessions.forEach(session => {
                  scheduleData.push({
                    week: weekNumber,
                    day: dayNum,
                    content: session.trim()
                  });
                });
              }
            });
          }

          console.log('Parsed schedule data:', scheduleData);
          console.log('Total sessions parsed:', scheduleData.length);
          console.log('=== EXCEL PARSING COMPLETE ===');

          // Group by week for the API format
          const groupedByWeek = {};
          scheduleData.forEach(entry => {
            if (!groupedByWeek[entry.week]) {
              groupedByWeek[entry.week] = {
                week: entry.week,
                days: {}
              };
            }

            // Support multiple sessions per day
            if (!groupedByWeek[entry.week].days[entry.day]) {
              groupedByWeek[entry.week].days[entry.day] = [];
            }
            groupedByWeek[entry.week].days[entry.day].push(entry.content);
          });

          // Convert to array format expected by API
          const apiFormat = Object.values(groupedByWeek).map(week => ({
            week: week.week,
            days: Object.entries(week.days).map(([day, contents]) => ({
              day: parseInt(day),
              sessions: contents
            }))
          }));

          console.log('API format:', apiFormat);

          resolve({
            raw: scheduleData,
            apiFormat: apiFormat,
            sheetName: sheetName,
            allSheetNames: workbook.SheetNames
          });

        } catch (error) {
          console.error('Excel parsing error:', error);
          reject(error);
        }
      };

      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        reject(error);
      };

      reader.readAsArrayBuffer(file);
    });
  },

  // Convert parsed Excel data to CSV format for preview
  excelDataToCsv: (parsedData) => {
    if (!parsedData || !parsedData.raw || parsedData.raw.length === 0) {
      return '';
    }

    // Find max week and day numbers
    let maxWeek = 0;
    let maxDay = 0;
    parsedData.raw.forEach(entry => {
      if (entry.week > maxWeek) maxWeek = entry.week;
      if (entry.day > maxDay) maxDay = entry.day;
    });

    // Build CSV header
    const headers = ['Day'];
    for (let w = 1; w <= maxWeek; w++) {
      headers.push(`Week ${w}`);
    }

    // Build rows
    const rows = [headers.join(',')];
    for (let d = 1; d <= maxDay; d++) {
      const row = [`Day ${d}`];
      for (let w = 1; w <= maxWeek; w++) {
        // Get ALL sessions for this day/week (not just the first one)
        const entries = parsedData.raw.filter(e => e.week === w && e.day === d);
        // Join multiple sessions with <br> for display
        const content = entries.map(e => e.content).join('<br>');
        // Escape content for CSV (wrap in quotes if contains comma, newline, or <br>)
        const escapedContent = content.includes(',') || content.includes('\n') || content.includes('<br>')
          ? `"${content.replace(/"/g, '""')}"`
          : content;
        row.push(escapedContent);
      }
      rows.push(row.join(','));
    }

    return rows.join('\n');
  },

  // Status to CSS class mapping for calendar events
  getStatusClass: (status) => {
    const statusMap = {
      'scheduled': 'event-scheduled',      // cyan
      'completed': 'event-completed',      // green
      'cancelled': 'event-cancelled',      // red
      'rescheduled': 'event-rescheduled',  // amber
      'draft': 'event-draft',              // gray
      'published': 'event-published'       // primary
    };
    return statusMap[status?.toLowerCase()] || 'event-scheduled';
  },

  // Convert batch schedule data to calendar events
  // holidayDates: Set of date strings in YYYY-MM-DD format
  formatScheduleForCalendar: (scheduleData, batchStartDate, holidayDates = new Set()) => {
    console.log('formatScheduleForCalendar called with:', {
      scheduleDataLength: scheduleData?.length,
      batchStartDate,
      holidayCount: holidayDates.size,
      firstEntry: scheduleData?.[0]
    });

    if (!Array.isArray(scheduleData) || scheduleData.length === 0) {
      console.warn('No schedule data to format for calendar');
      return [];
    }

    const events = scheduleData.map(entry => {
      const eventDate = entry.session_date || batchScheduleService.calculateSessionDate(
        batchStartDate,
        entry.week_number,
        entry.day_number,
        holidayDates
      );

      const statusClass = batchScheduleService.getStatusClass(entry.status);

      return {
        id: `schedule-${entry.id}`,
        title: entry.session_content || 'Session',
        start: eventDate,
        className: statusClass,
        extendedProps: {
          ...entry,
          type: 'batch-schedule'
        }
      };
    });

    console.log('Formatted calendar events:', events.length, 'events');
    return events;
  },

  // Calculate actual session date based on batch start date, week, and day
  // Business days exclude: Sundays, holidays, and 2nd & 4th Saturdays of each month
  // holidayDates: Set of date strings in YYYY-MM-DD format
  // Uses UTC methods throughout to avoid timezone-related off-by-one errors
  calculateSessionDate: (batchStartDate, weekNumber, dayNumber, holidayDates = new Set()) => {
    console.log('calculateSessionDate called with:', { batchStartDate, weekNumber, dayNumber, holidayCount: holidayDates.size });

    if (!batchStartDate) {
      console.warn('No batch start date provided, using today as fallback');
      batchStartDate = new Date().toISOString().split('T')[0];
    }

    // Parse date as UTC, stripping any time/timezone component
    const dateOnly = String(batchStartDate).split('T')[0];
    const [year, month, day] = dateOnly.split('-').map(Number);
    const startDate = new Date(Date.UTC(year, month - 1, day));

    if (isNaN(startDate.getTime())) {
      console.error('Invalid batch start date:', batchStartDate);
      return new Date().toISOString().split('T')[0]; // Fallback to today
    }

    // Helper to format date as YYYY-MM-DD for holiday lookup
    const formatDate = (date) => date.toISOString().split('T')[0];

    // Helper to check if a Saturday is the 2nd or 4th Saturday of the month
    const isSecondOrFourthSaturday = (date) => {
      if (date.getUTCDay() !== 6) return false; // Not a Saturday

      const dayOfMonth = date.getUTCDate();
      const saturdayNumber = Math.ceil(dayOfMonth / 7);
      return saturdayNumber === 2 || saturdayNumber === 4;
    };

    // Helper to check if a date is a business day
    // Excludes: Sundays, holidays, and 2nd & 4th Saturdays
    const isBusinessDay = (date) => {
      const dayOfWeek = date.getUTCDay();
      const dateStr = formatDate(date);

      // Sunday = 0
      if (dayOfWeek === 0) return false;

      // Check if it's a holiday
      if (holidayDates.has(dateStr)) return false;

      // Check if it's 2nd or 4th Saturday
      if (isSecondOrFourthSaturday(date)) return false;

      return true;
    };

    // Calculate total business days to add (approximately 5-6 business days per week)
    const totalBusinessDays = (weekNumber - 1) * 6 + (dayNumber - 1);

    // Add business days, skipping non-business days
    const sessionDate = new Date(startDate);
    let businessDaysAdded = 0;

    while (businessDaysAdded < totalBusinessDays) {
      sessionDate.setUTCDate(sessionDate.getUTCDate() + 1);
      if (isBusinessDay(sessionDate)) {
        businessDaysAdded++;
      }
    }

    // If the final date lands on a non-business day, move to next business day
    while (!isBusinessDay(sessionDate)) {
      sessionDate.setUTCDate(sessionDate.getUTCDate() + 1);
    }

    console.log('Calculated session date:', formatDate(sessionDate));
    return formatDate(sessionDate);
  },

  // Convert schedule data to table format for display (transposed: days as rows, weeks as columns)
  formatScheduleForTable: (scheduleData) => {
    if (!Array.isArray(scheduleData)) return {};
    
    console.log('=== FORMAT SCHEDULE FOR TABLE ===');
    console.log('Input schedule data:', scheduleData);
    console.log('Number of entries:', scheduleData.length);
    
    const tableData = {};
    
    // Initialize structure with days as keys
    for (let day = 1; day <= 6; day++) {
      tableData[`Day ${day}`] = {};
    }
    
    scheduleData.forEach((entry, index) => {
      const dayKey = `Day ${entry.day_number}`;
      const weekKey = `Week ${entry.week_number}`;
      
      console.log(`Entry ${index}: ${dayKey}, ${weekKey} - "${entry.session_content}"`);
      
      if (tableData[dayKey]) {
        // Initialize as array if not exists
        if (!tableData[dayKey][weekKey]) {
          tableData[dayKey][weekKey] = [];
        }
        
        // Add session to the array
        tableData[dayKey][weekKey].push({
          content: entry.session_content,
          date: entry.session_date,
          trainer: entry.trainer?.name,
          status: entry.status,
          id: entry.id
        });
        
        console.log(`Added to ${dayKey}[${weekKey}]. Array now has ${tableData[dayKey][weekKey].length} sessions`);
      }
    });
    
    console.log('Final table data structure:', tableData);
    return tableData;
  }
};

export default batchScheduleService;
